import { useState, useEffect, useMemo, useRef } from 'react';
import { Badge } from 'reactstrap';
import TableBuilder, { ModalButtonBuilder, FlexInputBuilder, ToastBuilder, ButtonBuilder, ApiRequest } from '../../../../../../../../GlobalComponents/Builders';
import { capitalizeFirstLetter } from '../../../../../../../../GlobalComponents/Helpers';
import { Trash2, Play, Pause, Plus, Clock, Settings, Zap } from 'react-feather';
import DatePicker from 'react-datepicker';

// ─── Toast Durations ───
const TOAST_SUCCESS_MS = 3500;
const TOAST_ERROR_MS = 10000;

// ─── Cron Presets ───
const FREQUENCY_PRESETS = [
    { label: 'Every minute', value: '* * * * ? *' },
    { label: 'Every 2 minutes', value: '0/2 * * * ? *' },
    { label: 'Every 5 minutes', value: '0/5 * * * ? *' },
    { label: 'Every 10 minutes', value: '0/10 * * * ? *' },
    { label: 'Every 15 minutes', value: '0/15 * * * ? *' },
    { label: 'Every 30 minutes', value: '0/30 * * * ? *' },
    { label: 'Every hour', value: '0 * * * ? *' },
    { label: 'Off-hours (6PM-6AM M-F)', value: '0/1 0-5,18-23 ? * MON-FRI *' },
    { label: 'Weekends only', value: '0/5 * ? * SAT-SUN *' },
    { label: 'Custom', value: 'custom' },
];

// Sync-specific presets
const SYNC_FREQUENCY_PRESETS = [
    { label: 'Even minutes (0,2,4...)', value: '0/2 * * * ? *' },
    { label: 'Odd minutes (1,3,5...)', value: '1/2 * * * ? *' },
    { label: 'Every minute', value: '* * * * ? *' },
    { label: 'Every 5 minutes', value: '0/5 * * * ? *' },
    { label: 'Custom', value: 'custom' },
];

const PROCESSING_OPTIONS = [
    { value: 'overrideLock', label: 'Override Lock' },
    { value: 'fetchUpdates', label: 'Fetch Updates' },
    { value: 'dryRun', label: 'Dry Run' },
];

// ─── Composite Status Groups ───
// Composite status-filter options come from dmt_state rows of type 'composite'
// (delivered via the /dmt/settings payload as composite_states). Each row's `config`
// JSON carries the sentinel `token` and display `label`. This built-in list is only a
// fallback used if composite_states is empty (e.g. before the composite seed is applied).
const STATUS_GROUP_FALLBACK = [
    { name: 'default', config: { token: '__none__', label: 'Default (include new records only)' } },
    { name: 'all_pending', config: { token: '__all_pending__', label: 'All pending (new, staged, re-staged, dry run)' } },
    { name: 'terminal_only', config: { token: '__terminal_only__', label: 'None (use terminal states only)' } },
];

// Parse a composite row's config (may be a JSON string or object) into { token, label }.
const parseCompositeConfig = (row) => {
    let config = row?.config;
    if (typeof config === 'string') {
        try { config = JSON.parse(config); } catch { config = null; }
    }
    return config || {};
};

const ORDER_DIRECTIONS = [
    { value: 'ASC', label: 'ASC' },
    { value: 'DESC', label: 'DESC' },
];

const FILTER_OPERATORS = [
    { value: 'in', label: 'in' },
    { value: 'not in', label: 'not in' },
    { value: '=', label: '=' },
    { value: '!=', label: '!=' },
    { value: '>', label: '>' },
    { value: '<', label: '<' },
    { value: 'is null', label: 'is null' },
    { value: 'is empty', label: 'is empty' },
    { value: 'is not null', label: 'is not null' },
];

// Filter sources. 'resource_data' = JSON path into the record payload (default).
// 'directive' = the migration-only `directive` column (no sync equivalent).
const FILTER_SOURCES = [
    { value: 'resource_data', label: 'resource_data' },
    { value: 'directive', label: 'directive' },
];

// Known directive values (migration records). 'delete' is the only directive
// the processor acts on today; '' / is null matches records with no directive.
const DIRECTIVE_OPTIONS = [
    { value: 'delete', label: 'delete' },
];

const ORDER_BY_FIELDS = [
    { value: 'updated_at', label: 'updated_at' },
    { value: 'last_staged_at', label: 'last_staged_at' },
    { value: 'source_rec_id', label: 'source_rec_id' },
    { value: 'destination_rec_id', label: 'destination_rec_id' },
    { value: 'status', label: 'status' },
    { value: 'action', label: 'action' },
    { value: 'parent_rec_id', label: 'parent_rec_id' },
];

// ─── Schedule List + Manager ───
const ScheduleModal = ({
    jobData,
    jobResourceMappings,
    handleDMTAction,
    dmtStates,
    dmtCompositeStates = [],
}) => {
    const [schedules, setSchedules] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const formSubmitRef = useRef(null);
    const modalTogglerRef = useRef(null);

    // Composite status groups come from the settings payload (dmt_state type 'composite').
    // Fall back to the built-in list if none are configured yet.
    const statusGroups = (dmtCompositeStates && dmtCompositeStates.length > 0) ? dmtCompositeStates : STATUS_GROUP_FALLBACK;

    const isSyncJob = jobData?.job_type === 'two-way-sync';
    const scheduleType = isSyncJob ? 'sync' : 'migration';

    // Resource options from mappings (non-setup only for migrations)
    const resourceOptions = useMemo(() => {
        const mappings = jobResourceMappings?.current || [];
        return mappings
            .filter(m => !isSyncJob || m.is_setup !== 1)
            .map(m => ({
                value: m.source_resource_id,
                label: `${capitalizeFirstLetter(m.source_resource_name)} → ${capitalizeFirstLetter(m.destination_resource_name)}`,
            }))
            .filter((v, i, a) => a.findIndex(x => x.value === v.value) === i);
    }, [jobResourceMappings, isSyncJob]);

    const loadSchedules = () => {
        setIsLoading(true);
        ApiRequest('GET', '/dmt/jobs/{jobId}/schedules', {
            pathParams: { jobId: jobData.id },
        }).then((results) => {
            setIsLoading(false);
            if (results?.data?.success) {
                setSchedules(results.data.schedules || []);
            }
        });
    };

    const toggleSchedule = (uid, resourceId, isActive) => {
        handleDMTAction('schedule-toggle', null, (results) => {
            if (results?.data?.success) {
                ToastBuilder('success', results.data.message, {}, TOAST_SUCCESS_MS);
                loadSchedules();
            } else {
                ToastBuilder('error', results?.data?.message || 'Failed to toggle', {}, TOAST_ERROR_MS);
            }
        }, { uid, resourceId, isActive: isActive ? 0 : 1 });
    };

    const deleteSchedule = (uid, resourceId) => {
        handleDMTAction('schedule-delete', null, (results) => {
            if (results?.data?.success) {
                ToastBuilder('success', results.data.message, {}, TOAST_SUCCESS_MS);
                loadSchedules();
            } else {
                ToastBuilder('error', results?.data?.message || 'Failed to delete', {}, TOAST_ERROR_MS);
            }
        }, { uid, resourceId });
    };

    const pingSchedule = (uid, resourceId) => {
        handleDMTAction('schedule-ping', null, (results) => {
            if (results?.data?.success) {
                ToastBuilder('success', results.data.message || 'Schedule pinged', {}, TOAST_SUCCESS_MS);
            } else {
                ToastBuilder('error', results?.data?.message || 'Failed to ping schedule', {}, TOAST_ERROR_MS);
            }
        }, { uid, resourceId });
    };

    return (
        <ModalButtonBuilder
            title={<><Clock size={14} style={{ verticalAlign: 'middle', marginRight: 5, marginTop: -2 }} /><span style={{ verticalAlign: 'middle', fontWeight: 600 }}>Schedules</span></>}
            className='btn-outline-info'
            modalTitle={`${isSyncJob ? 'Sync' : 'Migration'} Schedules — Job ${jobData.id}`}
            size='lg'
            onClick={loadSchedules}
            onSubmit={false}
            onCancelLabel='Close'
            body={
                <div>
                    {/* Existing Schedules */}
                    {isLoading ? <p className='text-muted'>Loading...</p> : (
                        schedules.length > 0 ? (
                            <TableBuilder
                                data={schedules}
                                processing={false}
                                pagination={false}
                                title=''
                                columns={[
                                    {
                                        name: 'Resource',
                                        selector: (row) => row.resource_name || row.resource_id,
                                        sortable: true,
                                    },
                                    {
                                        name: 'Description',
                                        selector: (row) => row.description || row.uid,
                                        sortable: true,
                                    },
                                    {
                                        name: 'Cron',
                                        selector: (row) => (row.cron_expressions || []).join(' | '),
                                        cell: (row) => <code style={{ fontSize: 11 }}>{(row.cron_expressions || []).join(' | ')}</code>,
                                    },
                                    {
                                        name: 'Status',
                                        selector: (row) => row.is_active,
                                        cell: (row) => <Badge color={row.is_active ? 'success' : 'secondary'}>{row.is_active ? 'Active' : 'Disabled'}</Badge>,
                                        center: true,
                                        width: '17%',
                                    },
                                    {
                                        name: 'Actions',
                                        cell: (row) => {
                                            const editRef = useRef(null);
                                            const editTogglerRef = useRef(null);
                                            return (
                                                <div className='d-flex gap-3 align-items-center justify-content-center'>
                                                    {row.is_active
                                                        ? <Pause size={14} style={{ cursor: 'pointer', color: '#6c757d', display: 'block' }} onClick={() => toggleSchedule(row.uid, row.resource_id, true)} title='Disable' />
                                                        : <Play size={14} style={{ cursor: 'pointer', color: '#28a745', display: 'block' }} onClick={() => toggleSchedule(row.uid, row.resource_id, false)} title='Enable' />
                                                    }
                                                    <Zap size={14} style={{ cursor: 'pointer', color: '#ffc107', display: 'block' }} onClick={() => pingSchedule(row.uid, row.resource_id)} title='Ping (run once now)' />
                                                    <ModalButtonBuilder
                                                        title={<Settings size={14} style={{ color: '#17a2b8' }} />}
                                                        className='btn-link p-0'
                                                        color={null}
                                                        buttonStyle={{ lineHeight: 0, display: 'flex', alignItems: 'center' }}
                                                        modalTitle='Edit Schedule'
                                                        size='md'
                                                        onSubmitLabel='Save Changes'
                                                        customSubmit={(toggler) => {
                                                            editTogglerRef.current = toggler;
                                                            if (editRef.current) editRef.current();
                                                        }}
                                                        onCancelLabel='Cancel'
                                                        body={
                                                            <ScheduleCreateForm
                                                                jobData={jobData}
                                                                scheduleType={scheduleType}
                                                                resourceOptions={resourceOptions}
                                                                handleDMTAction={handleDMTAction}
                                                                onCreated={() => { if (editTogglerRef.current) editTogglerRef.current(); loadSchedules(); }}
                                                                submitRef={editRef}
                                                                editMode={true}
                                                                initialData={row}
                                                                dmtStates={dmtStates}
                                                                statusGroups={statusGroups}
                                                            />
                                                        }
                                                    />
                                                    <ModalButtonBuilder
                                                        title={<Trash2 size={14} style={{ color: '#dc3545' }} />}
                                                        className='btn-link p-0'
                                                        color={null}
                                                        buttonStyle={{ lineHeight: 0, display: 'flex', alignItems: 'center' }}
                                                        modalTitle='Delete Schedule'
                                                        onSubmitLabel='Delete'
                                                        onCancelLabel='Cancel'
                                                        customSubmit={(toggler) => { deleteSchedule(row.uid, row.resource_id); toggler(); }}
                                                        body={<p>Delete this schedule? This removes the EventBridge schedule and cannot be undone.</p>}
                                                    />
                                                </div>
                                            );
                                        },
                                        center: true,
                                        width: '20%',
                                    },
                                ]}
                            />
                        ) : <p className='text-muted'>No schedules configured for this job.</p>
                    )}

                    <div className='mt-3'>
                        <ModalButtonBuilder
                            title={<><Plus size={13} /> New Schedule</>}
                            className='btn-primary btn-sm'
                            modalTitle={`New ${isSyncJob ? 'Sync' : 'Migration'} Schedule`}
                            size='md'
                            onSubmitLabel='Create Schedule'
                            customSubmit={(toggler) => {
                                modalTogglerRef.current = toggler;
                                if (formSubmitRef.current) formSubmitRef.current();
                            }}
                            onCancelLabel='Cancel'
                            body={
                                <ScheduleCreateForm
                                    jobData={jobData}
                                    scheduleType={scheduleType}
                                    resourceOptions={resourceOptions}
                                    handleDMTAction={handleDMTAction}
                                    onCreated={() => { if (modalTogglerRef.current) modalTogglerRef.current(); loadSchedules(); }}
                                    submitRef={formSubmitRef}
                                    dmtStates={dmtStates}
                                    statusGroups={statusGroups}
                                />
                            }
                        />
                    </div>
                </div>
            }
        />
    );
};

// ─── Create/Edit Form ───
const ScheduleCreateForm = ({ jobData, scheduleType, resourceOptions, handleDMTAction, onCreated, submitRef, editMode = false, initialData = null, dmtStates = [], statusGroups = STATUS_GROUP_FALLBACK }) => {
    const params = initialData?.params || {};
    const initOptions = [];
    if (editMode) {
        if (params.dryRun) initOptions.push('dryRun');
        if (params.overrideLock) initOptions.push('overrideLock');
        if (params.fetchUpdates) initOptions.push('fetchUpdates');
    } else {
        initOptions.push('dryRun');
    }

    const isSyncJob = scheduleType === 'sync';
    const presets = isSyncJob ? SYNC_FREQUENCY_PRESETS : FREQUENCY_PRESETS;

    const [resourceId, setResourceId] = useState(editMode ? initialData?.resource_id : null);
    const [code, setCode] = useState(editMode ? (initialData?.uid || '') : '');
    const [description, setDescription] = useState(editMode ? (initialData?.description || '') : '');
    const [cronPreset, setCronPreset] = useState(() => {
        if (!editMode) return null;
        const stored = (initialData?.cron_expressions || []).join(' | ');
        const match = presets.find(p => p.value === stored);
        return match ? match.value : 'custom';
    });
    const [customCron, setCustomCron] = useState(() => {
        if (!editMode) return '';
        const stored = (initialData?.cron_expressions || []).join(' | ');
        const match = presets.find(p => p.value === stored);
        return match ? '' : stored;
    });
    const [limit, setLimit] = useState(editMode ? (params.limit || 10) : 10);
    const [statusInclusions, setStatusInclusions] = useState(editMode ? (params.statusInclusions || []) : []);
    const [statusFilter, setStatusFilter] = useState(() => {
        if (!editMode) return ['__none__'];
        if (params.allPending) return ['__all_pending__'];
        if (params.terminalOnly) return ['__terminal_only__'];
        if (params.statusFilter?.length > 0) return params.statusFilter;
        return ['__none__'];
    });
    const [activeStatuses, setActiveStatuses] = useState([]);
    const [terminalStatuses, setTerminalStatuses] = useState([]);
    const [showTerminalStates, setShowTerminalStates] = useState(editMode && (params.statusInclusions || []).length > 0);
    const [showDateRange, setShowDateRange] = useState(editMode && (initialData?.start_date || initialData?.end_date));
    const [startDate, setStartDate] = useState(editMode ? (initialData?.start_date || '') : '');
    const [endDate, setEndDate] = useState(editMode ? (initialData?.end_date || '') : '');
    const [options, setOptions] = useState(initOptions);
    const [orderBy, setOrderBy] = useState(editMode && params.orderBy?.length ? params.orderBy : [{ field: 'updated_at', direction: 'ASC' }]);
    const [filters, setFilters] = useState(editMode ? (params.filters || []) : []);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Re-entrancy guard: blocks double/triple submits while a request is in flight.
    // A ref is used (not state) so rapid successive clicks are caught synchronously.
    const submitInFlightRef = useRef(false);

    // Derive active vs terminal statuses from pre-fetched dmtStates
    useEffect(() => {
        const terminalCategories = ['resolved', 'cancelled'];
        const active = (dmtStates || [])
            .filter(s => !terminalCategories.includes(s.category) && s.type === 'status' && parseInt(s.is_active) === 1)
            .map(s => ({ value: s.name, label: capitalizeFirstLetter(s.name) }));
        const terminal = (dmtStates || [])
            .filter(s => terminalCategories.includes(s.category) && s.type === 'status' && parseInt(s.is_active) === 1)
            .map(s => ({ value: s.name, label: capitalizeFirstLetter(s.name) }));
        setActiveStatuses(active);
        setTerminalStatuses(terminal);
    }, [dmtStates]);

    // Composite config keyed by sentinel token: { token: { name, label, resolve } }.
    // Drives options and the sentinels list. The backend expands tokens at query time,
    // so the form only needs to send the tokens — no member resolution here.
    const compositeByToken = useMemo(() => {
        const map = {};
        (statusGroups || [])
            .filter(g => parseInt(g.is_active ?? 1) === 1)
            .forEach(g => {
                const config = parseCompositeConfig(g);
                if (!config.token) return;
                map[config.token] = {
                    name: g.name,
                    label: config.label || capitalizeFirstLetter(g.name),
                    resolve: config.resolve || 'default',
                };
            });
        return map;
    }, [statusGroups]);

    // Token(s) whose resolve === 'none' require at least one terminal inclusion.
    const terminalOnlyTokens = useMemo(() => (
        Object.entries(compositeByToken).filter(([, c]) => c.resolve === 'none').map(([token]) => token)
    ), [compositeByToken]);

    // Sentinel tokens available (data-driven — no hardcoded token list).
    const sentinelTokens = useMemo(() => Object.keys(compositeByToken), [compositeByToken]);

    // Composite status-group options (sentinels) shown in the Status Filter dropdown.
    const statusGroupOptions = useMemo(() => (
        (statusGroups || [])
            .filter(g => parseInt(g.is_active ?? 1) === 1)
            .map(g => {
                const config = parseCompositeConfig(g);
                return { value: config.token, label: config.label || capitalizeFirstLetter(g.name) };
            })
            .filter(o => !!o.value)
    ), [statusGroups]);

    // Default sync to even/odd based on job_id
    useEffect(() => {
        if (isSyncJob && jobData.sync_job_id) {
            const isLowerJob = parseInt(jobData.id) < parseInt(jobData.sync_job_id);
            setCronPreset(isLowerJob ? '0/2 * * * ? *' : '1/2 * * * ? *');
        }
    }, [isSyncJob, jobData]);

    // Auto-expand terminal states section when "None (terminal states only)" is selected
    useEffect(() => {
        if (statusFilter.includes('__terminal_only__')) {
            setShowTerminalStates(true);
        }
    }, [statusFilter]);

    const handleStatusFilterChange = (newValues) => {
        const sentinels = sentinelTokens;
        const prevHadSentinel = statusFilter.some(v => sentinels.includes(v));
        const newHasSentinel = newValues.some(v => sentinels.includes(v));

        if (newHasSentinel && !prevHadSentinel) {
            // User just added a sentinel — keep only the sentinel they picked
            setStatusFilter(newValues.filter(v => sentinels.includes(v)).slice(-1));
        } else if (newHasSentinel && prevHadSentinel) {
            // User added a real status while sentinel was selected — remove sentinels
            const realStatuses = newValues.filter(v => !sentinels.includes(v));
            setStatusFilter(realStatuses.length > 0 ? realStatuses : newValues.filter(v => sentinels.includes(v)).slice(-1));
        } else {
            setStatusFilter(newValues);
        }
    };

    const addOrderBy = () => setOrderBy([...orderBy, { field: 'source_rec_id', direction: 'ASC' }]);
    const removeOrderBy = (idx) => setOrderBy(orderBy.filter((_, i) => i !== idx));
    const updateOrderBy = (idx, key, value) => {
        const updated = [...orderBy];
        updated[idx] = { ...updated[idx], [key]: value };
        setOrderBy(updated);
    };

    const addFilter = () => setFilters([...filters, { source: 'resource_data', field: '', op: 'in', value: '' }]);
    const removeFilter = (idx) => setFilters(filters.filter((_, i) => i !== idx));
    const updateFilter = (idx, key, value) => {
        const updated = [...filters];
        updated[idx] = { ...updated[idx], [key]: value };
        setFilters(updated);
    };

    const handleSubmit = () => {
        // Block re-entry while a create/update request is already in flight.
        if (submitInFlightRef.current) return;

        if (!resourceId) return ToastBuilder('error', 'Select a resource');

        const cronExpr = cronPreset === 'custom' ? customCron : cronPreset;
        if (!cronExpr) return ToastBuilder('error', 'Select a frequency');

        // Validate code if provided (6-20 alphanumeric chars) — only on create
        if (!editMode && code && (code.length < 6 || code.length > 20 || !/^[a-zA-Z0-9-]+$/.test(code))) {
            return ToastBuilder('error', 'Code must be 6-20 alphanumeric characters or dashes');
        }

        // Validate date range
        if (endDate && !startDate) return ToastBuilder('error', 'Start date is required when end date is set');

        // Validate status filter (required for migrations)
        if (!isSyncJob && statusFilter.length === 0) return ToastBuilder('error', 'Status filter is required');
        // A terminal-only token (resolve: 'none') requires at least one terminal inclusion.
        if (!isSyncJob && statusFilter.some(s => terminalOnlyTokens.includes(s)) && statusInclusions.length === 0) {
            return ToastBuilder('error', 'Select at least one terminal state when using "None (use terminal states only)"');
        }

        const cronExpressions = cronExpr.split('|').map(c => c.trim()).filter(Boolean);

        // Tokenized statusFilter: send composite tokens and/or literal statuses as-is
        // (de-duped). The queuer resolves tokens to their member statuses at query time,
        // so no terminalOnly/allPending flags are needed for new schedules.
        const effectiveStatusFilter = [...new Set(statusFilter)];

        const params = {
            dryRun: options.includes('dryRun'),
            overrideLock: options.includes('overrideLock'),
            fetchUpdates: options.includes('fetchUpdates'),
            limit: parseInt(limit) || 10,
            statusFilter: effectiveStatusFilter,
            statusInclusions,
            orderBy: orderBy.filter(o => o.field),
            filters: filters
                .filter(f => {
                    const isDirect = f.source === 'directive';
                    // Direct-column filters (e.g. directive) need no field path.
                    const hasTarget = isDirect || f.field;
                    const hasValue = f.value || f.op === 'is null' || f.op === 'is empty' || f.op === 'is not null';
                    return hasTarget && hasValue;
                })
                .map(f => ({
                    source: f.source || 'resource_data',
                    field: f.source === 'directive' ? '' : f.field,
                    op: f.op,
                    value: ['is null', 'is empty', 'is not null'].includes(f.op) ? null : (typeof f.value === 'string' && f.value.includes(',')) ? f.value.split(',').map(v => v.trim()) : f.value,
                })),
        };

        const actionName = editMode ? 'schedule-update' : 'schedule-create';
        const formData = editMode ? {
            uid: initialData.uid,
            resourceId: initialData.resource_id,
            description,
            cronExpressions,
            startDate: startDate || null,
            endDate: endDate || null,
            isActive: initialData.is_active,
            params,
        } : {
            resourceId,
            code: code || null,
            description,
            scheduleType,
            cronExpressions,
            startDate: startDate || null,
            endDate: endDate || null,
            params,
        };

        submitInFlightRef.current = true;
        setIsSubmitting(true);
        handleDMTAction(actionName, null, (results) => {
            submitInFlightRef.current = false;
            setIsSubmitting(false);
            if (results?.data?.success) {
                ToastBuilder('success', results.data.message, {}, TOAST_SUCCESS_MS);
                onCreated();
            } else {
                ToastBuilder('error', results?.data?.message || `Failed to ${editMode ? 'update' : 'create'} schedule`, {}, TOAST_ERROR_MS);
            }
        }, formData);
    };

    // Expose submit to parent via ref
    useEffect(() => {
        if (submitRef) submitRef.current = handleSubmit;
    });

    return (
        <div className='input-md'>
            {/* Row 1: Resource + Frequency */}
            {editMode ? (
                <FlexInputBuilder inputSize='md' inputs={[
                    { name: 'resource', label: 'Resource', type: 'text', value: resourceOptions.find(o => o.value === resourceId)?.label || resourceId, disabled: true, size: 7 },
                    { name: 'frequency', label: 'Frequency', type: 'reactselect', value: cronPreset, onChange: setCronPreset, options: presets, placeholder: 'Select frequency...', required: true, size: 5 },
                ]} />
            ) : (
                <FlexInputBuilder inputSize='md' inputs={[
                    { name: 'resource', label: 'Resource', type: 'reactselect', value: resourceId, onChange: setResourceId, options: resourceOptions, placeholder: 'Select resource...', required: true, size: 7 },
                    { name: 'frequency', label: 'Frequency', type: 'reactselect', value: cronPreset, onChange: setCronPreset, options: presets, placeholder: 'Select frequency...', required: true, size: 5 },
                ]} />
            )}

            {cronPreset === 'custom' && (
                <>
                    <FlexInputBuilder inputSize='md' inputs={[
                        { name: 'customCron', label: 'Custom Cron', type: 'text', value: customCron, onChange: setCustomCron, placeholder: '0/1 0-5,18-23 ? * MON-FRI *  (use | for composite)', style: { fontFamily: 'monospace' } },
                    ]} />
                    <div className='mb-2' style={{ fontSize: 11, color: '#999' }}>
                        Examples:{' '}
                        {[
                            { label: 'Every 3 min', value: '0/3 * * * ? *' },
                            { label: 'Off-hours M-F', value: '0/1 0-5,18-23 ? * MON-FRI *' },
                            { label: 'Weekends', value: '0/5 * ? * SAT-SUN *' },
                            { label: 'Daily 2AM', value: '0 2 * * ? *' },
                            { label: 'Hourly :30', value: '30 * * * ? *' },
                        ].map((ex, i) => (
                            <span key={i}>
                                {i > 0 && ' · '}
                                <a href='#' className='txt-secondary' style={{ textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); setCustomCron(ex.value); }}>{ex.label}</a>
                            </span>
                        ))}
                    </div>
                </>
            )}

            {/* Row 2: Code + Limit */}
            <FlexInputBuilder inputSize='md' inputs={[
                { name: 'code', label: editMode ? 'Code' : 'Code (optional)', type: 'text', value: code, onChange: (v) => setCode(v.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 20)), placeholder: '6-20 alphanumeric chars or dashes', size: isSyncJob ? 12 : 9, disabled: editMode },
                ...(!isSyncJob ? [{ name: 'limit', label: 'Limit', type: 'text', value: limit, onChange: (v) => setLimit(v.replace(/[^0-9]/g, '')), size: 3 }] : []),
            ]} />

            {/* Options (shown for both sync and migration) */}
            <FlexInputBuilder inputSize='md' inputs={[
                { name: 'options', label: 'Options', type: 'multiselect', value: options, onChange: setOptions, options: PROCESSING_OPTIONS, placeholder: 'Select options...' },
            ]} />

            {!isSyncJob && (
                <>
                    {/* Status Filter */}
                    <FlexInputBuilder inputSize='md' inputs={[
                        { name: 'statusFilter', label: 'Status Filter', type: 'multiselect', value: statusFilter, onChange: handleStatusFilterChange, options: [...statusGroupOptions, ...activeStatuses], placeholder: 'Select statuses to queue...', required: true },
                    ]} />

                    {/* Order By */}
                    <div className='row input-md'>
                        <div className='col-md-12 mb-2'>
                            <label className='mb-0' style={{ fontSize: 12, fontWeight: 600 }}>Order By</label>
                            {orderBy.map((o, idx) => (
                                <div key={idx} className='d-flex gap-1 mb-1 align-items-center'>
                                    <FlexInputBuilder layout='inline' inputSize='md' inputs={[
                                        { name: `orderBy-field-${idx}`, type: 'reactselect', value: o.field, onChange: (v) => updateOrderBy(idx, 'field', v), options: ORDER_BY_FIELDS, placeholder: 'Select column...', flex: '1 1 auto' },
                                        { name: `orderBy-dir-${idx}`, type: 'reactselect', value: o.direction, onChange: (v) => updateOrderBy(idx, 'direction', v), options: ORDER_DIRECTIONS, width: 90, isSearchable: false },
                                    ]} style={{ flex: '1 1 auto' }} />
                                    {orderBy.length > 1 && <Trash2 size={14} style={{ cursor: 'pointer', color: '#dc3545' }} onClick={() => removeOrderBy(idx)} />}
                                </div>
                            ))}
                            <ButtonBuilder label='+ Add' className='btn-outline-secondary btn-sm' style={{ fontSize: 11 }} onClick={addOrderBy} useSpinner={false} />
                        </div>
                    </div>

                    {/* Filters */}
                    <div className='mb-2'>
                        <label className='mb-0' style={{ fontSize: 12, fontWeight: 600 }}>Filters</label>
                        {filters.map((f, idx) => {
                            const isDirective = f.source === 'directive';
                            const isValuelessOp = ['is null', 'is empty', 'is not null'].includes(f.op);
                            return (
                                <div key={idx} className='d-flex gap-1 mb-1 align-items-center'>
                                    <FlexInputBuilder layout='inline' inputSize='md' inputs={[
                                        {
                                            name: `filter-source-${idx}`, type: 'reactselect', value: f.source || 'resource_data',
                                            onChange: (v) => {
                                                // Switching source resets the target/value so stale
                                                // resource_data paths don't leak into a directive filter.
                                                updateFilter(idx, 'source', v);
                                                updateFilter(idx, 'field', '');
                                                updateFilter(idx, 'value', '');
                                            },
                                            options: FILTER_SOURCES, width: 130, isSearchable: false,
                                        },
                                        // resource_data: free-text JSON path. directive: column, no path.
                                        ...(isDirective ? [] : [
                                            { name: `filter-field-${idx}`, type: 'text', value: f.field, onChange: (v) => updateFilter(idx, 'field', v), placeholder: 'e.g. board/id', flex: '0 0 30%' },
                                        ]),
                                        { name: `filter-op-${idx}`, type: 'reactselect', value: f.op, onChange: (v) => updateFilter(idx, 'op', v), options: FILTER_OPERATORS, width: 100, isSearchable: false },
                                        // directive: pick from known directive values. resource_data: free text.
                                        ...(isValuelessOp ? [] : isDirective ? [
                                            { name: `filter-value-${idx}`, type: 'reactselect', value: f.value, onChange: (v) => updateFilter(idx, 'value', v), options: DIRECTIVE_OPTIONS, placeholder: 'Select directive...', flex: '1 1 auto' },
                                        ] : [
                                            { name: `filter-value-${idx}`, type: 'text', value: f.value, onChange: (v) => updateFilter(idx, 'value', v), placeholder: 'Value (comma-separated for in/not in)', flex: '1 1 auto' },
                                        ]),
                                    ]} style={{ flex: '1 1 auto' }} />
                                    <Trash2 size={14} style={{ cursor: 'pointer', color: '#dc3545' }} onClick={() => removeFilter(idx)} />
                                </div>
                            );
                        })}
                        <div className='mt-0'>
                            <ButtonBuilder label='+ Add Filter' className='btn-outline-secondary btn-sm' style={{ fontSize: 11 }} onClick={addFilter} useSpinner={false} />
                        </div>
                    </div>

                    {/* Terminal States (collapsed) */}
                    <div className='mb-2'>
                        {!showTerminalStates ? (
                            <ButtonBuilder label='+ Include terminal states' className='btn-outline-danger btn-sm' style={{ fontSize: 11 }} onClick={() => setShowTerminalStates(true)} useSpinner={false} />
                        ) : (
                            <FlexInputBuilder inputSize='md' inputs={[
                                { name: 'statusInclusions', label: 'Include Terminal States', type: 'multiselect', value: statusInclusions, onChange: setStatusInclusions, options: terminalStatuses, placeholder: 'Records in these states will also be queued...' },
                            ]} />
                        )}
                    </div>
                </>
            )}

            {/* Start/End Date (collapsed) */}
            <div className='mb-2'>
                {!showDateRange ? (
                    <ButtonBuilder label='+ Set start/end date' className='btn-outline-secondary btn-sm' style={{ fontSize: 11 }} onClick={() => setShowDateRange(true)} useSpinner={false} />
                ) : (
                    <div className='row datetime-picker'>
                        <div className='col-md-6 mb-1'>
                            <label style={{ fontSize: 12, fontWeight: 600 }}>Start Date</label>
                            <DatePicker
                                selected={startDate ? new Date(startDate) : null}
                                onChange={(date) => setStartDate(date ? date.toISOString() : '')}
                                showTimeSelect
                                dateFormat='MMM d, yyyy h:mm aa'
                                placeholderText='Select start date...'
                                className='form-control form-control-sm'
                                isClearable
                                wrapperClassName='w-100'
                                popperProps={{ strategy: 'fixed' }}
                            />
                        </div>
                        <div className='col-md-6 mb-1'>
                            <label style={{ fontSize: 12, fontWeight: 600 }}>End Date</label>
                            <DatePicker
                                selected={endDate ? new Date(endDate) : null}
                                onChange={(date) => setEndDate(date ? date.toISOString() : '')}
                                showTimeSelect
                                dateFormat='MMM d, yyyy h:mm aa'
                                placeholderText='Select end date...'
                                className='form-control form-control-sm'
                                isClearable
                                wrapperClassName='w-100'
                                popperProps={{ strategy: 'fixed' }}
                                minDate={startDate ? new Date(startDate) : null}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Description (optional, at bottom) */}
            <FlexInputBuilder inputSize='md' inputs={[
                { name: 'description', label: 'Description (optional)', type: 'text', value: description, onChange: setDescription, placeholder: 'e.g. "Error retry for board 25 tickets"' },
            ]} />
        </div>
    );
};

export default ScheduleModal;
